import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import type { AuthChangeEvent, Session } from '@supabase/supabase-js';
import { shouldRequestInitialPassword, type AuthCallbackKind } from '../lib/auth';
import { bootstrapAuthSession, initialAuthCallbackKind, isDemoMode, supabase } from '../lib/supabase';
import type { Role } from '../types';

export interface AuthProfile {
  id: string;
  displayName: string;
  role: Role;
}

interface AuthState {
  ready: boolean;
  session: Session | null;
  profile: AuthProfile | null;
  needsPasswordSetup: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  setInitialPassword: (password: string) => Promise<boolean>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(isDemoMode);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<AuthProfile | null>(null);
  const [needsPasswordSetup, setNeedsPasswordSetup] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const callbackKind = useRef<AuthCallbackKind>(initialAuthCallbackKind);

  useEffect(() => {
    const client = supabase;
    if (isDemoMode || !client) return;
    let active = true;
    let revision = 0;
    let bootstrapComplete = false;

    const loadProfile = async (event: AuthChangeEvent, nextSession: Session | null) => {
      const currentRevision = ++revision;
      setReady(false);
      setSession(nextSession);
      setProfile(null);
      setNeedsPasswordSetup(false);
      setError(null);

      let nextProfile: AuthProfile | null = null;
      if (nextSession) {
        const result = await client.from('profiles').select('id, display_name, role').eq('user_id', nextSession.user.id).eq('active', true).single();
        if (!active || currentRevision !== revision) return;
        if (result.error) {
          setError('Seu acesso existe, mas o perfil DLX ainda nao foi configurado. Fale com o gestor.');
        } else {
          nextProfile = { id: result.data.id, displayName: result.data.display_name, role: result.data.role as Role };
          setProfile(nextProfile);
        }
      }

      if (!active || currentRevision !== revision) return;
      setNeedsPasswordSetup(shouldRequestInitialPassword({
        event,
        session: nextSession,
        hasProfile: Boolean(nextProfile),
        callbackKind: callbackKind.current,
      }));
      setReady(true);
    };

    // Eventos recebidos durante a restauracao explicita sao ignorados; o resultado
    // do bootstrap e a fonte da verdade para o primeiro estado da pagina.
    const { data: listener } = client.auth.onAuthStateChange((event, nextSession) => {
      if (!bootstrapComplete) return;
      void loadProfile(event, nextSession);
    });

    void bootstrapAuthSession().then((result) => {
      if (!active) return;
      bootstrapComplete = true;
      callbackKind.current = result.callbackKind;
      if (result.error) {
        setSession(null);
        setProfile(null);
        setNeedsPasswordSetup(false);
        setError('Este link nao pode mais ser usado. Solicite um novo link ao gestor.');
        setReady(true);
        return;
      }
      const initialEvent: AuthChangeEvent = result.callbackKind === 'recovery' ? 'PASSWORD_RECOVERY' : 'INITIAL_SESSION';
      void loadProfile(initialEvent, result.session);
    });

    return () => {
      active = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    if (!supabase) return;
    setError(null);
    const result = await supabase.auth.signInWithPassword({ email, password });
    if (result.error) setError('E-mail ou senha invalidos. No primeiro acesso, abra o link do convite recebido por e-mail.');
  };
  const setInitialPassword = async (password: string) => {
    if (!supabase || !session) return false;
    setError(null);
    const result = await supabase.auth.updateUser({
      password,
      data: { ...session.user.user_metadata, dlx_password_initialized: true },
    });
    if (result.error) {
      setError('Nao foi possivel salvar a senha. Solicite um novo convite ao gestor.');
      return false;
    }
    callbackKind.current = null;
    setNeedsPasswordSetup(false);
    setError('Senha criada com seguranca. Seu perfil DLX agora precisa ser liberado pelo gestor.');
    return true;
  };
  const signOut = async () => { if (supabase) await supabase.auth.signOut(); };

  return <AuthContext.Provider value={{ ready, session, profile, needsPasswordSetup, error, signIn, setInitialPassword, signOut }}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth deve ser usado dentro de AuthProvider');
  return value;
}

